import React, { useState } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Code,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  Star,
  Shield,
  TrendingUp,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import technologyStackMasterService from '../../services/technologyStackMasterService'

const TechnologyStackMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [formData, setFormData] = useState({
    technologyStackCode: '',
    technologyStackName: '',
    technologyStackDescription: '',
    technologyCategory: 'Frontend',
    technologyType: 'Framework',
    technologyStackStatus: 'Active',
    version: '',
    releaseDate: '',
    endOfLifeDate: '',
    compatibleTechnologies: [],
    dependencies: [],
    performanceRating: 5,
    scalabilityRating: 5,
    learningCurve: 'Medium',
    minimumTeamSize: 1,
    recommendedTeamSize: 3,
    skillLevelRequired: 'Mid-level',
    licensingModel: 'Open Source',
    estimatedCost: 0,
    costCurrency: 'USD',
    documentationQuality: 'Good',
    communitySupport: 'Good',
    officialSupport: false,
    marketShare: 0,
    adoptionRate: 'Stable',
    suitableProjectTypes: [],
    suitableProjectSizes: [],
    securityRating: 'Good',
    complianceStandards: []
  })

  const [loading, setLoading] = useState(false)

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const addDependency = () => {
    setFormData(prev => ({
      ...prev,
      dependencies: [...prev.dependencies, { name: '', version: '', isRequired: true }]
    }))
  }

  const updateDependency = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.map((dep, i) => 
        i === index ? { ...dep, [field]: value } : dep
      )
    }))
  }

  const removeDependency = (index) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.technologyStackCode.trim()) {
      toast.error('Technology Stack Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.technologyStackCode.trim())) {
      toast.error('Technology Stack Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.technologyStackName.trim()) {
      toast.error('Technology Stack Name is required')
      return false
    }
    if (!formData.technologyStackDescription.trim()) {
      toast.error('Technology Stack Description is required')
      return false
    }
    if (formData.performanceRating < 1 || formData.performanceRating > 10) {
      toast.error('Performance Rating must be between 1 and 10')
      return false
    }
    if (formData.scalabilityRating < 1 || formData.scalabilityRating > 10) {
      toast.error('Scalability Rating must be between 1 and 10')
      return false
    }
    if (formData.marketShare < 0 || formData.marketShare > 100) {
      toast.error('Market Share must be between 0 and 100')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Technology Stack Master record...')
      const response = await technologyStackMasterService.createTechnologyStackMaster({
        technologyStackCode: formData.technologyStackCode.toUpperCase(),
        technologyStackName: formData.technologyStackName,
        technologyStackDescription: formData.technologyStackDescription,
        technologyCategory: formData.technologyCategory,
        technologyType: formData.technologyType,
        technologyStackStatus: formData.technologyStackStatus,
        version: formData.version,
        releaseDate: formData.releaseDate || null,
        endOfLifeDate: formData.endOfLifeDate || null,
        compatibleTechnologies: formData.compatibleTechnologies,
        dependencies: formData.dependencies,
        performanceRating: formData.performanceRating,
        scalabilityRating: formData.scalabilityRating,
        learningCurve: formData.learningCurve,
        minimumTeamSize: formData.minimumTeamSize,
        recommendedTeamSize: formData.recommendedTeamSize,
        skillLevelRequired: formData.skillLevelRequired,
        licensingModel: formData.licensingModel,
        estimatedCost: formData.estimatedCost,
        costCurrency: formData.costCurrency,
        documentationQuality: formData.documentationQuality,
        communitySupport: formData.communitySupport,
        officialSupport: formData.officialSupport,
        marketShare: formData.marketShare,
        adoptionRate: formData.adoptionRate,
        suitableProjectTypes: formData.suitableProjectTypes,
        suitableProjectSizes: formData.suitableProjectSizes,
        securityRating: formData.securityRating,
        complianceStandards: formData.complianceStandards
      })

      if (response.success) {
        toast.success('Technology Stack Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/technology-stack-master')
      } else {
        toast.error(response.message || 'Failed to create Technology Stack Master record')
      }
    } catch (error) {
      console.error('Error creating Technology Stack Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Technology Stack Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Technology Stack Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      technologyStackCode: '',
      technologyStackName: '',
      technologyStackDescription: '',
      technologyCategory: 'Frontend',
      technologyType: 'Framework',
      technologyStackStatus: 'Active',
      version: '',
      releaseDate: '',
      endOfLifeDate: '',
      compatibleTechnologies: [],
      dependencies: [],
      performanceRating: 5,
      scalabilityRating: 5,
      learningCurve: 'Medium',
      minimumTeamSize: 1,
      recommendedTeamSize: 3,
      skillLevelRequired: 'Mid-level',
      licensingModel: 'Open Source',
      estimatedCost: 0,
      costCurrency: 'USD',
      documentationQuality: 'Good',
      communitySupport: 'Good',
      officialSupport: false,
      marketShare: 0,
      adoptionRate: 'Stable',
      suitableProjectTypes: [],
      suitableProjectSizes: [],
      securityRating: 'Good',
      complianceStandards: []
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/technology-stack-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Technology Stack Master', path: '/projects/technology-stack-master', onClick: () => navigate('/projects/technology-stack-master') },
    { label: 'Create New', path: '/projects/technology-stack-master/new', isCurrent: true }
  ]

  const categoryOptions = [
    'Frontend', 'Backend', 'Database', 'DevOps', 'Mobile', 'Cloud', 'AI/ML', 'Other'
  ]

  const typeOptions = [
    'Framework', 'Library', 'Language', 'Database', 'Tool', 'Platform', 'Service', 'Other'
  ]

  const statusOptions = [
    'Active', 'Inactive', 'Deprecated', 'Legacy'
  ]

  const learningCurveOptions = [
    'Easy', 'Medium', 'Hard', 'Expert'
  ]

  const skillLevelOptions = [
    'Junior', 'Mid-level', 'Senior', 'Expert'
  ]

  const licensingOptions = [
    'Open Source', 'Commercial', 'Freemium', 'Enterprise', 'Free'
  ]

  const qualityOptions = [
    'Excellent', 'Good', 'Fair', 'Poor'
  ]

  const adoptionRateOptions = [
    'Growing', 'Stable', 'Declining', 'New'
  ]

  const projectTypeOptions = [
    'Web Development', 'Mobile Development', 'Desktop Application', 'API Development', 
    'Data Science', 'AI/ML', 'DevOps', 'Cloud Migration', 'Legacy Modernization'
  ]

  const projectSizeOptions = [
    'Small', 'Medium', 'Large', 'Enterprise'
  ]

  const currencyOptions = [
    'USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'
  ]

  const helpContent = (
    <>
      <h3>What is Technology Stack Master?</h3>
      <p>Technology Stack Master lets you <strong>create and manage technology stack information</strong>—e.g. React, Node.js, MongoDB—with category, type, version, specifications, performance ratings, cost &amp; licensing, and compatibility.</p>

      <h3>Basic Information</h3>
      <p><strong>Required (*):</strong></p>
      <ul>
        <li><strong>Technology Stack Code</strong> – Uppercase letters, numbers, hyphens only (e.g. REACT, NODEJS, MONGODB), max 20 characters.</li>
        <li><strong>Technology Stack Name</strong> – Display name (e.g. React, Node.js, MongoDB), max 100 characters.</li>
        <li><strong>Technology Category</strong> – Frontend, Backend, Database, DevOps, Mobile, Cloud, AI/ML, or Other.</li>
        <li><strong>Technology Type</strong> – Framework, Library, Language, Database, Tool, Platform, Service, or Other.</li>
        <li><strong>Technology Stack Description</strong> – Purpose and key features, max 500 characters.</li>
      </ul>
      <p><strong>Optional:</strong> <strong>Status</strong> (Active, Inactive, Deprecated, Legacy), <strong>Version</strong> (e.g. 18.2.0, 3.11.0), <strong>Release Date</strong>, <strong>End of Life Date</strong>.</p>

      <h3>Specifications</h3>
      <p><strong>Learning Curve</strong> – Easy, Medium, Hard, or Expert. <strong>Skill Level Required</strong> – Junior, Mid-level, Senior, or Expert. <strong>Minimum Team Size</strong> and <strong>Recommended Team Size</strong> (1–50). <strong>Compatible Technologies</strong> – Add/remove list. <strong>Dependencies</strong> – Add/remove rows with Dependency name, Version, and Required checkbox.</p>

      <h3>Performance &amp; Ratings</h3>
      <p><strong>Performance Rating</strong> and <strong>Scalability Rating</strong> (1–10). <strong>Market Share</strong> (0–100%). <strong>Documentation Quality</strong>, <strong>Community Support</strong>, <strong>Security Rating</strong> – Excellent, Good, Fair, or Poor. <strong>Adoption Rate</strong> – Growing, Stable, Declining, or New. <strong>Official Support Available</strong> – checkbox.</p>

      <h3>Cost &amp; Licensing</h3>
      <p><strong>Licensing Model</strong> – Open Source, Commercial, Freemium, Enterprise, or Free. <strong>Estimated Cost</strong> (numeric). <strong>Currency</strong> – USD, EUR, GBP, INR, CAD, or AUD.</p>

      <h3>Compatibility &amp; Usage</h3>
      <p><strong>Suitable Project Types</strong> – Checkboxes (e.g. Web Development, Mobile Development, API Development, Data Science, AI/ML, DevOps, Cloud Migration, Legacy Modernization). <strong>Suitable Project Sizes</strong> – Small, Medium, Large, or Enterprise. <strong>Compliance Standards</strong> – Add/remove list (e.g. GDPR, SOC 2).</p>

      <h3>How to use</h3>
      <p>Fill all required fields in <strong>Basic Information</strong>. Use the tabs to complete Specifications, Performance &amp; Ratings, Cost &amp; Licensing, and Compatibility &amp; Usage as needed. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear the form or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Technology Stack Master Entry</h1>
          <p>Create and manage technology stack information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Technology Stack Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Code size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'specifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('specifications')}
          >
            <Settings size={18} />
            <span>Specifications</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <Star size={18} />
            <span>Performance & Ratings</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'cost' ? 'active' : ''}`}
            onClick={() => setActiveTab('cost')}
          >
            <DollarSign size={18} />
            <span>Cost & Licensing</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'compatibility' ? 'active' : ''}`}
            onClick={() => setActiveTab('compatibility')}
          >
            <CheckSquare size={18} />
            <span>Compatibility & Usage</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Technology Stack Code, Name, Category, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="technologyStackCode" className="form-label required">
                    Technology Stack Code
                  </label>
                  <input
                    id="technologyStackCode"
                    type="text"
                    value={formData.technologyStackCode}
                    onChange={(e) => handleInputChange('technologyStackCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., REACT, NODEJS, MONGODB"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyStackName" className="form-label required">
                    Technology Stack Name
                  </label>
                  <input
                    id="technologyStackName"
                    type="text"
                    value={formData.technologyStackName}
                    onChange={(e) => handleInputChange('technologyStackName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., React, Node.js, MongoDB"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyCategory" className="form-label required">
                    Technology Category
                  </label>
                  <select
                    id="technologyCategory"
                    value={formData.technologyCategory}
                    onChange={(e) => handleInputChange('technologyCategory', e.target.value)}
                    className="form-input"
                  >
                    {categoryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="technologyType" className="form-label required">
                    Technology Type
                  </label>
                  <select
                    id="technologyType"
                    value={formData.technologyType}
                    onChange={(e) => handleInputChange('technologyType', e.target.value)}
                    className="form-input"
                  >
                    {typeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Status, Version, Release Date, End of Life Date */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="technologyStackStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="technologyStackStatus"
                    value={formData.technologyStackStatus}
                    onChange={(e) => handleInputChange('technologyStackStatus', e.target.value)}
                    className="form-input"
                  >
                    {statusOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="version" className="form-label">
                    Version
                  </label>
                  <input
                    id="version"
                    type="text"
                    value={formData.version}
                    onChange={(e) => handleInputChange('version', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 18.2.0, 3.11.0"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="releaseDate" className="form-label">
                    Release Date
                  </label>
                  <input
                    id="releaseDate"
                    type="date"
                    value={formData.releaseDate}
                    onChange={(e) => handleInputChange('releaseDate', e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="endOfLifeDate" className="form-label">
                    End of Life Date
                  </label>
                  <input
                    id="endOfLifeDate"
                    type="date"
                    value={formData.endOfLifeDate}
                    onChange={(e) => handleInputChange('endOfLifeDate', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 3: Technology Stack Description */}
              <div className="form-group">
                <label htmlFor="technologyStackDescription" className="form-label required">
                  Technology Stack Description
                </label>
                <textarea
                  id="technologyStackDescription"
                  value={formData.technologyStackDescription}
                  onChange={(e) => handleInputChange('technologyStackDescription', e.target.value)}
                  className="form-input"
                  placeholder="Describe the technology stack, its purpose, and key features..."
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {/* Specifications Tab */}
          {activeTab === 'specifications' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Technical Specifications</h3>
              </div>
              
              {/* Row 1: Learning Curve, Skill Level, Team Sizes */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="learningCurve" className="form-label">
                    Learning Curve
                  </label>
                  <select
                    id="learningCurve"
                    value={formData.learningCurve}
                    onChange={(e) => handleInputChange('learningCurve', e.target.value)}
                    className="form-input"
                  >
                    {learningCurveOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="skillLevelRequired" className="form-label">
                    Skill Level Required
                  </label>
                  <select
                    id="skillLevelRequired"
                    value={formData.skillLevelRequired}
                    onChange={(e) => handleInputChange('skillLevelRequired', e.target.value)}
                    className="form-input"
                  >
                    {skillLevelOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="minimumTeamSize" className="form-label">
                    Minimum Team Size
                  </label>
                  <input
                    id="minimumTeamSize"
                    type="number"
                    value={formData.minimumTeamSize}
                    onChange={(e) => handleInputChange('minimumTeamSize', parseInt(e.target.value) || 1)}
                    className="form-input"
                    min="1"
                    max="50"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="recommendedTeamSize" className="form-label">
                    Recommended Team Size
                  </label>
                  <input
                    id="recommendedTeamSize"
                    type="number"
                    value={formData.recommendedTeamSize}
                    onChange={(e) => handleInputChange('recommendedTeamSize', parseInt(e.target.value) || 3)}
                    className="form-input"
                    min="1"
                    max="50"
                  />
                </div>
              </div>

              {/* Compatible Technologies */}
              <div className="form-group">
                <label className="form-label">Compatible Technologies</label>
                <div className="array-input-container">
                  {formData.compatibleTechnologies.map((tech, index) => (
                    <div key={index} className="array-input-item">
                      <input
                        type="text"
                        value={tech}
                        onChange={(e) => {
                          const newTechs = [...formData.compatibleTechnologies]
                          newTechs[index] = e.target.value
                          handleArrayChange('compatibleTechnologies', newTechs)
                        }}
                        className="form-input"
                        placeholder="Enter compatible technology"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('compatibleTechnologies', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('compatibleTechnologies', '')}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Technology
                  </button>
                </div>
              </div>

              {/* Dependencies */}
              <div className="form-group">
                <label className="form-label">Dependencies</label>
                <div className="dependencies-container">
                  {formData.dependencies.map((dep, index) => (
                    <div key={index} className="dependency-item">
                      <input
                        type="text"
                        value={dep.name}
                        onChange={(e) => updateDependency(index, 'name', e.target.value)}
                        className="form-input"
                        placeholder="Dependency name"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <input
                        type="text"
                        value={dep.version}
                        onChange={(e) => updateDependency(index, 'version', e.target.value)}
                        className="form-input"
                        placeholder="Version"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={dep.isRequired}
                          onChange={(e) => updateDependency(index, 'isRequired', e.target.checked)}
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={() => removeDependency(index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDependency}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Dependency
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Performance & Ratings Tab */}
          {activeTab === 'performance' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Performance & Quality Ratings</h3>
              </div>
              
              {/* Row 1: Performance Rating, Scalability Rating, Market Share */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="performanceRating" className="form-label">
                    Performance Rating (1-10)
                  </label>
                  <input
                    id="performanceRating"
                    type="number"
                    value={formData.performanceRating}
                    onChange={(e) => handleInputChange('performanceRating', parseInt(e.target.value) || 5)}
                    className="form-input"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="scalabilityRating" className="form-label">
                    Scalability Rating (1-10)
                  </label>
                  <input
                    id="scalabilityRating"
                    type="number"
                    value={formData.scalabilityRating}
                    onChange={(e) => handleInputChange('scalabilityRating', parseInt(e.target.value) || 5)}
                    className="form-input"
                    min="1"
                    max="10"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="marketShare" className="form-label">
                    Market Share (%)
                  </label>
                  <input
                    id="marketShare"
                    type="number"
                    value={formData.marketShare}
                    onChange={(e) => handleInputChange('marketShare', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
              </div>

              {/* Row 2: Documentation Quality, Community Support, Security Rating */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="documentationQuality" className="form-label">
                    Documentation Quality
                  </label>
                  <select
                    id="documentationQuality"
                    value={formData.documentationQuality}
                    onChange={(e) => handleInputChange('documentationQuality', e.target.value)}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="communitySupport" className="form-label">
                    Community Support
                  </label>
                  <select
                    id="communitySupport"
                    value={formData.communitySupport}
                    onChange={(e) => handleInputChange('communitySupport', e.target.value)}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="securityRating" className="form-label">
                    Security Rating
                  </label>
                  <select
                    id="securityRating"
                    value={formData.securityRating}
                    onChange={(e) => handleInputChange('securityRating', e.target.value)}
                    className="form-input"
                  >
                    {qualityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Adoption Rate, Official Support */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label htmlFor="adoptionRate" className="form-label">
                    Adoption Rate
                  </label>
                  <select
                    id="adoptionRate"
                    value={formData.adoptionRate}
                    onChange={(e) => handleInputChange('adoptionRate', e.target.value)}
                    className="form-input"
                  >
                    {adoptionRateOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.officialSupport}
                      onChange={(e) => handleInputChange('officialSupport', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Official Support Available</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Cost & Licensing Tab */}
          {activeTab === 'cost' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Cost & Licensing Information</h3>
              </div>
              
              {/* Row 1: Licensing Model, Estimated Cost, Currency */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="licensingModel" className="form-label">
                    Licensing Model
                  </label>
                  <select
                    id="licensingModel"
                    value={formData.licensingModel}
                    onChange={(e) => handleInputChange('licensingModel', e.target.value)}
                    className="form-input"
                  >
                    {licensingOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="estimatedCost" className="form-label">
                    Estimated Cost
                  </label>
                  <input
                    id="estimatedCost"
                    type="number"
                    value={formData.estimatedCost}
                    onChange={(e) => handleInputChange('estimatedCost', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="costCurrency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="costCurrency"
                    value={formData.costCurrency}
                    onChange={(e) => handleInputChange('costCurrency', e.target.value)}
                    className="form-input"
                  >
                    {currencyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Compatibility & Usage Tab */}
          {activeTab === 'compatibility' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Compatibility & Usage Information</h3>
              </div>
              
              {/* Suitable Project Types */}
              <div className="form-group">
                <label className="form-label">Suitable Project Types</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectTypeOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.suitableProjectTypes.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('suitableProjectTypes', option)
                          } else {
                            removeArrayItem('suitableProjectTypes', formData.suitableProjectTypes.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Suitable Project Sizes */}
              <div className="form-group">
                <label className="form-label">Suitable Project Sizes</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {projectSizeOptions.map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.suitableProjectSizes.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('suitableProjectSizes', option)
                          } else {
                            removeArrayItem('suitableProjectSizes', formData.suitableProjectSizes.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Compliance Standards */}
              <div className="form-group">
                <label className="form-label">Compliance Standards</label>
                <div className="array-input-container">
                  {formData.complianceStandards.map((standard, index) => (
                    <div key={index} className="array-input-item">
                      <input
                        type="text"
                        value={standard}
                        onChange={(e) => {
                          const newStandards = [...formData.complianceStandards]
                          newStandards[index] = e.target.value
                          handleArrayChange('complianceStandards', newStandards)
                        }}
                        className="form-input"
                        placeholder="Enter compliance standard (e.g., GDPR, SOC 2)"
                        style={{ flex: 1, margin: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('complianceStandards', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('complianceStandards', '')}
                    className="btn-secondary"
                  >
                    <Plus size={16} />
                    Add Standard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Technology Stack'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Technology Stack Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default TechnologyStackMasterForm
